import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Files } from 'lucide-react';
import ContractFormModal from './ContractFormModal';
import BulkContractModal from './BulkContractModal';
import MissingFieldsModal from './MissingFieldsModal';
import GenerateMenu from './GenerateMenu';
import './ZZPShared.css';
import './Contracts.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  return fetch(url, { ...options, headers, credentials: 'include' });
}

async function generateContract(id, type, format) {
  const token = localStorage.getItem('ozel_token');
  const res = await fetch(`/api/zzp/contracts/${id}/generate?type=${type}&format=${format}`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) {
    alert('Generatie mislukt');
    return;
  }
  const ct = res.headers.get('Content-Type') || '';
  if (ct.startsWith('text/html')) {
    alert('Generatie mislukt (server gaf HTML terug)');
    return;
  }
  const blob = await res.blob();
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  const cd = res.headers.get('Content-Disposition') || '';
  const m  = cd.match(/filename="?([^"]+)"?/);
  a.download = m ? m[1] : `${type}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Delay revoke — Safari and Chrome may still be reading the blob when click() returns
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// preflight + generate: returns { blocked, missing } if validation failed, or runs the download
async function preflightAndGenerate(id, type, format, onBlocked) {
  try {
    const pre = await authFetch(`/api/zzp/contracts/${id}/preflight?type=${type}`, { method: 'POST' });
    if (pre.ok) {
      const data = await pre.json().catch(() => ({}));
      const missing = Array.isArray(data?.missing) ? data.missing : [];
      if (missing.length) {
        onBlocked(missing);
        return;
      }
    }
  } catch {
    // Network failure on preflight: proceed to generate anyway
  }
  return generateContract(id, type, format);
}

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function Contracts() {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkOpen, setBulkOpen]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [format, setFormat]       = useState({}); // { [contractId]: 'pdf' | 'docx' }
  const [missingModal, setMissingModal] = useState(null); // { missing: [...] } | null

  const runGenerate = (id, type, fmt) =>
    preflightAndGenerate(id, type, fmt, (missing) => setMissingModal({ missing }));

  const load = async () => {
    setLoading(true);
    try {
      const res  = await authFetch('/api/zzp/contracts');
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = async (row) => {
    try {
      const res = await authFetch(`/api/zzp/contracts/${row.id}`);
      if (res.status === 404) {
        alert('Dit contract bestaat niet meer. De lijst wordt vernieuwd.');
        await load();
        return;
      }
      const data = await res.json();
      setEditing(data || row);
      setModalOpen(true);
    } catch {
      setEditing(row);
      setModalOpen(true);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleSaved = async () => {
    await load();
    closeModal();
  };

  const handleDelete = async (row, e) => {
    e.stopPropagation();
    if (!confirm(`Contract voor "${row.worker_naam || ''}" definitief verwijderen?`)) return;
    try {
      const res = await authFetch(`/api/zzp/contracts/${row.id}`, { method: 'DELETE' });
      // 404 = row already gone → treat as success (idempotent delete)
      if (!res.ok && res.status !== 404) throw new Error('Verwijderen mislukt');
      setItems((p) => p.filter((x) => x.id !== row.id));
    } catch (err) {
      alert(err.message);
    }
  };

  const getFmt = (id) => format[id] || 'pdf';
  const setFmt = (id, v) => setFormat((p) => ({ ...p, [id]: v }));

  return (
    <div className="zzp-page">
      <div className="zzp-header">
        <div>
          <h2>Contracten</h2>
          <p>Modelovereenkomsten en toets-documenten voor ZZP'ers</p>
        </div>
        <div className="contract-header-actions">
          <button className="zzp-secondary-btn" onClick={() => setBulkOpen(true)}>
            <Files size={18} />
            Bulk contracten
          </button>
          <button className="zzp-primary-btn" onClick={openCreate}>
            <Plus size={18} />
            Nieuw Contract
          </button>
        </div>
      </div>

      {loading ? (
        <div className="zzp-empty">Laden...</div>
      ) : items.length === 0 ? (
        <div className="zzp-empty">Nog geen contracten. Klik op "Nieuw Contract" om te beginnen.</div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="zzp-card-list">
            {items.map((row) => (
              <div key={row.id} className="zzp-card">
                <div className="zzp-card-top">
                  <span className="zzp-card-name">{row.worker_naam || '—'}</span>
                  <div className="zzp-actions">
                    <button className="zzp-icon-btn" onClick={() => openEdit(row)} title="Bewerken">
                      <Pencil size={14} />
                    </button>
                    <button className="zzp-icon-btn danger" onClick={(e) => handleDelete(row, e)} title="Verwijderen">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {row.worker_bedrijfsnaam && (
                  <div className="zzp-card-row"><span className="zzp-card-label">Bedrijf</span> {row.worker_bedrijfsnaam}</div>
                )}
                <div className="zzp-card-row"><span className="zzp-card-label">Project</span> {row.project_naam || '—'}</div>
                <div className="zzp-card-row"><span className="zzp-card-label">Periode</span> {fmtDate(row.datum_in)} → {fmtDate(row.datum_uit)}</div>
                <div className="zzp-card-row"><span className="zzp-card-label">Tekendatum</span> {fmtDate(row.tekendatum)}</div>

                <div className="contract-generate-row">
                  <GenerateMenu
                    format={getFmt(row.id)}
                    onFormatChange={(f) => setFmt(row.id, f)}
                    onGenerate={(type) => runGenerate(row.id, type, getFmt(row.id))}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="zzp-table-wrap">
            <table className="zzp-table">
              <thead>
                <tr>
                  <th>Medewerker</th>
                  <th>Bedrijfsnaam</th>
                  <th>Project</th>
                  <th>Periode</th>
                  <th>Tekendatum</th>
                  <th>Aangemaakt</th>
                  <th>Genereren</th>
                  <th>Acties</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} className="zzp-row">
                    <td className="zzp-cell-strong" onClick={() => openEdit(row)}>{row.worker_naam || '—'}</td>
                    <td onClick={() => openEdit(row)}>{row.worker_bedrijfsnaam || '—'}</td>
                    <td onClick={() => openEdit(row)}>{row.project_naam || '—'}</td>
                    <td onClick={() => openEdit(row)}>{fmtDate(row.datum_in)} → {fmtDate(row.datum_uit)}</td>
                    <td onClick={() => openEdit(row)}>{fmtDate(row.tekendatum)}</td>
                    <td onClick={() => openEdit(row)}>{fmtDate(row.created_at)}</td>
                    <td>
                      <GenerateMenu
                        format={getFmt(row.id)}
                        onFormatChange={(f) => setFmt(row.id, f)}
                        onGenerate={(type) => runGenerate(row.id, type, getFmt(row.id))}
                      />
                    </td>
                    <td>
                      <div className="zzp-actions">
                        <button className="zzp-icon-btn" onClick={() => openEdit(row)} title="Bewerken">
                          <Pencil size={14} />
                        </button>
                        <button className="zzp-icon-btn danger" onClick={(e) => handleDelete(row, e)} title="Verwijderen">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {modalOpen && (
        <ContractFormModal
          contract={editing}
          onClose={closeModal}
          onSaved={handleSaved}
        />
      )}

      {bulkOpen && (
        <BulkContractModal
          onClose={() => setBulkOpen(false)}
          onSaved={async () => { await load(); setBulkOpen(false); }}
        />
      )}

      <MissingFieldsModal
        open={!!missingModal}
        onClose={() => setMissingModal(null)}
        missing={missingModal?.missing || []}
      />
    </div>
  );
}
